/**
 * @zendir/ui - LinkBudgetCard Component
 *
 * RF Signal Chain Visualization showing complete link budget equation.
 * Tx Power → Antenna Gains → Path Loss → Rx Power = Margin
 *
 * AstroUXDS Compliance:
 * - Uses status colors for semantic meaning (gains=green, losses=red)
 * - Tx Power uses Zendir accent color (brand identity)
 * - Margin indicator uses status colors based on link quality
 * - Typography follows minimum size requirements (14pt)
 *
 * Features:
 * - Full null-safety with graceful fallbacks
 * - Visual signal chain diagram
 * - Link margin indicator with progress bar
 * - Loading and empty states
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../theme";
import {
  safeNumber,
  formatDataRate,
  classNames,
  type StatusLevel,
} from "../utils";

export interface LinkBudgetData {
  /** Transmitter power in dBm */
  txPowerDbm: number;
  /** Transmitter antenna gain in dBi */
  txAntennaGainDbi: number;
  /** Receiver antenna gain in dBi */
  rxAntennaGainDbi: number;
  /** Free space path loss in dB (negative value) */
  pathLossDb: number;
  /** Atmospheric losses in dB (negative value) */
  atmosphericLossDb?: number;
  /** Pointing losses in dB (negative value) */
  pointingLossDb?: number;
  /** Received power in dBm */
  rxPowerDbm: number;
  /** Receiver sensitivity threshold in dBm */
  rxSensitivityDbm: number;
  /** Current range in km */
  rangeKm: number;
  /** Data rate in bps */
  dataRateBps?: number;
  /** Link status */
  isLinkClosed: boolean;
}

export interface LinkBudgetCardProps {
  /** Link budget data */
  linkBudget?: LinkBudgetData;
  /** Ground station name */
  groundStationName?: string;
  /** Compact mode */
  compact?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Format dB value with sign
 */
function formatDb(
  value: number | undefined,
  showSign = true,
  suffix = "dB",
): string {
  if (value === undefined || value === null || !Number.isFinite(value)) {
    return `-- ${suffix}`;
  }
  const sign = showSign && value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(1)} ${suffix}`;
}

/**
 * Calculate power in watts from dBm
 */
function dbmToWatts(dbm: number | undefined): string {
  if (dbm === undefined || !Number.isFinite(dbm)) return "-- W";
  const watts = Math.pow(10, (dbm - 30) / 10);
  if (watts >= 1000) return `${(watts / 1000).toFixed(1)} kW`;
  if (watts >= 1) return `${watts.toFixed(1)} W`;
  if (watts >= 0.001) return `${(watts * 1000).toFixed(1)} mW`;
  return `${(watts * 1000000).toFixed(1)} µW`;
}

/**
 * Get margin status level
 */
function getMarginStatus(margin: number | undefined): StatusLevel {
  if (margin === undefined || !Number.isFinite(margin)) return "off";
  if (margin >= 6) return "normal";
  if (margin >= 3) return "standby";
  if (margin >= 0) return "caution";
  return "critical";
}

/**
 * LinkBudgetCard - Visualizes RF link budget
 *
 * @example
 * ```tsx
 * <LinkBudgetCard
 *   linkBudget={linkBudgetData}
 *   groundStationName="Goldstone"
 * />
 * ```
 */
export const LinkBudgetCard = memo(function LinkBudgetCard({
  linkBudget,
  groundStationName = "Ground Station",
  compact = false,
  loading = false,
  className = "",
}: LinkBudgetCardProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};

  // Calculate margin
  const marginDb = useMemo(() => {
    if (!linkBudget) return undefined;
    const rx = linkBudget.rxPowerDbm;
    const sens = linkBudget.rxSensitivityDbm;
    if (!Number.isFinite(rx) || !Number.isFinite(sens)) return undefined;
    return rx - sens;
  }, [linkBudget?.rxPowerDbm, linkBudget?.rxSensitivityDbm]);

  const marginStatus = getMarginStatus(marginDb);
  const transitionDuration = prefersReducedMotion ? "0ms" : "300ms";

  // Medium-soft status colors (readable, not too pale)
  const SOFT_STATUS_COLORS: Record<StatusLevel, string> = {
    normal: "#6ed86e",
    caution: "#ffd54f",
    serious: "#ffa726",
    critical: "#e57373",
    off: "#90a4ae",
    standby: "#4fc3f7",
  };

  // Loading state
  if (loading) {
    return (
      <div
        className={classNames("zendir-link-budget-card", "loading", className)}
        role="article"
        aria-busy="true"
        aria-label="Loading link budget"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.md,
          minHeight: 280,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              height: 24,
              width: "40%",
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
          <div
            style={{
              height: 24,
              width: 60,
              backgroundColor: "rgba(255,255,255,0.1)",
              borderRadius: 4,
            }}
          />
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
              }}
            />
            <div
              style={{
                flex: 1,
                height: 16,
                backgroundColor: "rgba(255,255,255,0.06)",
                borderRadius: 4,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!linkBudget) {
    return (
      <div
        className={classNames("zendir-link-budget-card", "empty", className)}
        role="article"
        aria-label="No link budget data"
        style={{
          backgroundColor: cardBg,
          ...cardGlass,
          border: "none",
          borderRadius: tokens.borderRadius.lg,
          padding: tokens.spacing.lg,
          textAlign: "center",
          color: tokens.colors.text.tertiary,
        }}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="currentColor"
          style={{ opacity: 0.5, marginBottom: 8 }}
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
        </svg>
        <p style={{ margin: 0, fontSize: tokens.typography.body[2].fontSize }}>
          {groundStationName}: No link budget data
        </p>
      </div>
    );
  }

  const isLinkClosed = linkBudget.isLinkClosed ?? false;
  const statusColor = isLinkClosed
    ? SOFT_STATUS_COLORS.normal
    : SOFT_STATUS_COLORS.off;
  const marginColor =
    SOFT_STATUS_COLORS[marginStatus] ?? SOFT_STATUS_COLORS.off;

  const chainItemStyle: React.CSSProperties = {
    position: "relative",
    paddingLeft: 36,
    marginBottom: tokens.spacing.md,
  };

  const dotStyle = (color: string): React.CSSProperties => ({
    position: "absolute",
    left: 8,
    top: 6,
    width: 16,
    height: 16,
    backgroundColor: color,
    borderRadius: "50%",
    border: `2px solid ${tokens.colors.background.surface}`,
    zIndex: 10,
  });

  return (
    <article
      className={classNames("zendir-link-budget-card", className)}
      aria-label={`Link budget for ${groundStationName}`}
      style={{
        backgroundColor: cardBg,
        ...cardGlass,
        border: "none",
        borderRadius: tokens.borderRadius.lg,
        padding: tokens.spacing.md,
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
      }}
    >
      {/* Header */}
      {!compact && (
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: tokens.typography.fontSize.md,
                fontWeight: tokens.typography.fontWeight.semibold,
              }}
            >
              Link Budget
            </h3>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
              }}
            >
              {groundStationName}
            </p>
          </div>
          <span
            role="status"
            style={{
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.sm,
              fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
              fontWeight: tokens.typography.fontWeight.bold,
              backgroundColor: `${statusColor}20`,
              color: statusColor,
            }}
          >
            {isLinkClosed ? "Link Closed" : "No Link"}
          </span>
        </header>
      )}

      {/* Signal Chain */}
      <div style={{ position: "relative" }}>
        {/* Vertical line */}
        <div
          style={{
            position: "absolute",
            left: 14,
            top: 24,
            bottom: 24,
            width: 2,
            backgroundColor: tokens.colors.border.muted,
          }}
        />

        {/* Tx Power */}
        <ChainItem
          label="Tx Power"
          value={formatDb(linkBudget.txPowerDbm, false, "dBm")}
          subtext={dbmToWatts(linkBudget.txPowerDbm)}
          color={tokens.colors.accent.primary}
          dotStyle={dotStyle}
          chainItemStyle={chainItemStyle}
          tokens={tokens}
        />

        {/* Tx Antenna Gain */}
        <ChainItem
          label="Antenna Gain"
          value={formatDb(linkBudget.txAntennaGainDbi, true, "dBi")}
          color={SOFT_STATUS_COLORS.normal}
          dotStyle={dotStyle}
          chainItemStyle={chainItemStyle}
          tokens={tokens}
        />

        {/* Path Loss */}
        <ChainItem
          label="Path Loss"
          value={formatDb(linkBudget.pathLossDb, true, "dB")}
          subtext={`Range: ${safeNumber(linkBudget.rangeKm, 0, "--")} km`}
          color={SOFT_STATUS_COLORS.critical}
          dotStyle={dotStyle}
          chainItemStyle={chainItemStyle}
          tokens={tokens}
        />

        {/* Additional losses */}
        {(linkBudget.atmosphericLossDb || linkBudget.pointingLossDb) && (
          <ChainItem
            label="Other Losses"
            value={formatDb(
              (linkBudget.atmosphericLossDb ?? 0) +
                (linkBudget.pointingLossDb ?? 0),
              true,
              "dB",
            )}
            color={SOFT_STATUS_COLORS.caution}
            dotStyle={dotStyle}
            chainItemStyle={chainItemStyle}
            tokens={tokens}
          />
        )}

        {/* Rx Antenna Gain */}
        <ChainItem
          label="Rx Gain"
          value={formatDb(linkBudget.rxAntennaGainDbi, true, "dBi")}
          color={SOFT_STATUS_COLORS.normal}
          dotStyle={dotStyle}
          chainItemStyle={chainItemStyle}
          tokens={tokens}
        />

        {/* Divider */}
        <div
          style={{
            height: 1,
            backgroundColor: tokens.colors.border.muted,
            marginLeft: 36,
            marginBottom: tokens.spacing.sm,
          }}
        />

        {/* Rx Power Result */}
        <ChainItem
          label="Rx Power"
          value={formatDb(linkBudget.rxPowerDbm, false, "dBm")}
          color={
            isLinkClosed ? tokens.colors.text.primary : SOFT_STATUS_COLORS.off
          }
          glow={isLinkClosed}
          dotStyle={dotStyle}
          chainItemStyle={chainItemStyle}
          tokens={tokens}
          bold
        />
      </div>

      {/* Margin Indicator */}
      <div
        style={{
          marginTop: tokens.spacing.md,
          paddingTop: tokens.spacing.md,
          borderTop: `1px solid ${tokens.colors.border.muted}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: tokens.typography.fontSize.sm,
          }}
        >
          <span style={{ color: tokens.colors.text.tertiary }}>
            Link Margin
          </span>
          <span
            style={{
              fontFamily: tokens.typography.fontFamily.mono,
              fontWeight: tokens.typography.fontWeight.bold,
              fontVariantNumeric: "tabular-nums",
              color: marginColor,
            }}
          >
            {formatDb(marginDb, true, "dB")}
          </span>
        </div>

        {/* Visual margin bar */}
        <div
          style={{
            marginTop: tokens.spacing.sm,
            height: 8,
            backgroundColor: tokens.colors.border.muted,
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            role="progressbar"
            aria-valuenow={marginDb ?? 0}
            aria-label="Link margin"
            style={{
              height: "100%",
              borderRadius: 4,
              transition: `width ${transitionDuration} ease`,
              backgroundColor: marginColor,
              width: `${Math.min(100, Math.max(0, ((marginDb ?? 0) + 10) * 5))}%`,
            }}
          />
        </div>

        {/* Data rate */}
        {linkBudget.dataRateBps !== undefined && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: tokens.typography.fontSize.xs,
              marginTop: tokens.spacing.sm,
            }}
          >
            <span style={{ color: tokens.colors.text.tertiary }}>
              Data Rate
            </span>
            <span
              style={{
                fontFamily: tokens.typography.fontFamily.mono,
                color: SOFT_STATUS_COLORS.standby,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDataRate(linkBudget.dataRateBps)}
            </span>
          </div>
        )}
      </div>
    </article>
  );
});

// ============================================================================
// Helper Component
// ============================================================================

interface ChainItemProps {
  label: string;
  value: string;
  subtext?: string;
  color: string;
  glow?: boolean;
  bold?: boolean;
  dotStyle: (color: string) => React.CSSProperties;
  chainItemStyle: React.CSSProperties;
  tokens: ReturnType<typeof useTheme>["tokens"];
}

const ChainItem = memo(function ChainItem({
  label,
  value,
  subtext,
  color,
  glow = false,
  bold = false,
  dotStyle,
  chainItemStyle,
  tokens,
}: ChainItemProps): React.ReactElement {
  return (
    <div style={chainItemStyle}>
      <div
        style={{
          ...dotStyle(color),
          boxShadow: glow ? `0 0 8px ${color}` : "none",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: bold
            ? tokens.typography.fontWeight.semibold
            : tokens.typography.fontWeight.normal,
        }}
      >
        <span
          style={{
            color: bold
              ? tokens.colors.text.primary
              : tokens.colors.text.secondary,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontFamily: tokens.typography.fontFamily.mono,
            color: bold ? tokens.colors.text.primary : color,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </span>
      </div>
      {subtext && (
        <div
          style={{
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.tertiary,
          }}
        >
          {subtext}
        </div>
      )}
    </div>
  );
});
